import axios from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { Button } from '../button/button';
import { Icon } from '../icon/icon';

interface Project {
  id: string;
  name: string;
}

interface CloudProcessing {
  id: string;
  name: string;
}

enum Status {
  Idle,
  RequestPending,
  RequestFailed,
}

enum Service {
  Viewer = 'viewer',
  GcpEditor = 'gcp-editor',
  GeodataUpload = 'geodata-upload',
}

interface Props {
  onLinkDeleted: () => void;
  onLinkCreated: (link: string) => void;
}

const LinkGenerator = (props: Props) => {
  const { t } = useTranslation();
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [geodata, setGeodata] = React.useState<CloudProcessing[]>([]);

  const [selectedLanguage, setSelectedLanguage] = React.useState('sv');
  const [selectedService, setSelectedService] = React.useState(Service.Viewer);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string>();
  const [selectedGeodataId, setSelectedGeodataId] = React.useState<string>();

  const [projectStatus, setProjectStatus] = React.useState(Status.Idle);
  const [geodataStatus, setGeodataStatus] = React.useState(Status.Idle);
  const [createAccessTokenStatus, setCreateAccessTokenStatus] = React.useState(Status.Idle);

  const loadProjects = React.useCallback(async () => {
    const source = axios.CancelToken.source();

    try {
      setProjectStatus(Status.RequestPending);

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0(source);
      const response = await projectService.getAll({});

      setProjects(
        response.data
          .map((x) => ({
            id: x.id,
            name: x.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );

      setProjectStatus(Status.Idle);
    } catch (ex) {
      setProjectStatus(Status.RequestFailed);
    }

    return () => {
      source.cancel();
    };
  }, []);

  const loadGeodata = React.useCallback(async () => {
    if (!selectedProjectId || selectedService !== Service.GcpEditor) {
      return;
    }

    const source = axios.CancelToken.source();

    try {
      setGeodataStatus(Status.RequestPending);
      setGeodata([]);

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0(source);
      const response = await projectService.fetchProjectsGeodata({
        path: { projectId: selectedProjectId },
      });
      setGeodata(
        response.data
          .filter((x) =>
            ['user_move_flags', 'optimize_pending', 'optimize_running'].includes(x.status),
          )
          .sort((a, b) => a.uploaded.localeCompare(b.uploaded))
          .map((x) => ({
            id: x.id,
            name: x.name,
          })),
      );

      setGeodataStatus(Status.Idle);
    } catch (ex) {
      setGeodataStatus(Status.RequestFailed);
    }

    return () => {
      source.cancel();
    };
  }, [selectedProjectId, selectedService]);

  React.useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    void loadGeodata();
  }, [loadGeodata]);

  const resolveResourcePayload = () => {
    switch (selectedService) {
      case Service.GcpEditor:
        return [
          {
            name: 'geodataId',
            value: selectedGeodataId!,
          },
        ];

      case Service.GeodataUpload:
      case Service.Viewer:
        return [
          {
            name: 'projectId',
            value: selectedProjectId!,
          },
        ];
    }
  };

  const isCreateButtonEnabled = (): boolean => {
    switch (selectedService) {
      case Service.GcpEditor:
        return selectedProjectId !== undefined && selectedGeodataId !== undefined;

      case Service.GeodataUpload:
      case Service.Viewer:
        return selectedProjectId !== undefined;
    }
  };

  const createNewToken = async () => {
    try {
      setCreateAccessTokenStatus(Status.RequestPending);
      props.onLinkDeleted();

      const accessTokenService = SkyMapAxiosServiceFactory.instance.createAccessTokenServiceV0();
      const response = await accessTokenService.createAccessToken({
        body: {
          service: selectedService,
          resource: resolveResourcePayload(),
        },
      });

      const url = `${document.location.protocol}//${document.location.host}/redirectaccess/${response.data.accessToken}`;
      props.onLinkCreated(
        selectedLanguage === 'sv' ? url : `${url}#googtrans(${selectedLanguage})`,
      );

      setCreateAccessTokenStatus(Status.Idle);
    } catch (ex) {
      setCreateAccessTokenStatus(Status.RequestFailed);
    }
  };

  const renderProjects = () => {
    switch (projectStatus) {
      case Status.RequestPending:
        return (
          <span>
            <Icon icon={['fas', 'spinner']} spin={true} />
          </span>
        );

      case Status.Idle:
        return projects.length > 0 ? (
          <select
            id="select.projects"
            onChange={(e) => setSelectedProjectId(e.target.value || undefined)}
          >
            <option value="">
              -- {t('accessTokens.linkGenerator.selectProject', { ns: 'components' })} --
            </option>
            {projects.map((x) => {
              return (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              );
            })}
          </select>
        ) : (
          <i>{t('accessTokens.linkGenerator.noProjects', { ns: 'components' })}</i>
        );

      case Status.RequestFailed:
        return (
          <span className="loading-error">
            {t('accessTokens.linkGenerator.errorOccurred', { ns: 'components' })}
          </span>
        );
    }
  };

  const renderGeodata = () => {
    switch (geodataStatus) {
      case Status.RequestPending:
        return (
          <span>
            <Icon icon={['fas', 'spinner']} spin={true} />
          </span>
        );

      case Status.Idle:
        if (!selectedProjectId) {
          return (
            <select disabled={true}>
              <option value="">
                -- {t('accessTokens.linkGenerator.selectGeodata', { ns: 'components' })} --
              </option>
            </select>
          );
        }

        return geodata.length > 0 ? (
          <select
            id="select.geodata"
            onChange={(e) => setSelectedGeodataId(e.target.value || undefined)}
          >
            <option value="">-- Välj molnprocessering --</option>
            {geodata.map((x) => {
              return (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              );
            })}
          </select>
        ) : (
          <i>{t('accessTokens.linkGenerator.noGeodata', { ns: 'components' })}</i>
        );

      case Status.RequestFailed:
        return (
          <span className="loading-error">
            {t('accessTokens.linkGenerator.errorOccurred', { ns: 'components' })}
          </span>
        );
    }
  };

  const renderCreateButton = () => {
    switch (createAccessTokenStatus) {
      case Status.RequestPending:
        return (
          <Button
            color="primary"
            loading={true}
            variant="contained"
            onClick={() => createNewToken()}
          >
            {t('accessTokens.linkGenerator.createLink', { ns: 'components' })}
          </Button>
        );

      case Status.Idle:
        return (
          <Button
            color="primary"
            disabled={!isCreateButtonEnabled()}
            variant="contained"
            onClick={() => createNewToken()}
          >
            {t('accessTokens.linkGenerator.createLink', { ns: 'components' })}
          </Button>
        );

      case Status.RequestFailed:
        return (
          <Button
            color="error"
            disabled={!isCreateButtonEnabled()}
            variant="contained"
            onClick={() => createNewToken()}
          >
            {t('accessTokens.linkGenerator.errorOccurredRetry', { ns: 'components' })}
          </Button>
        );
    }
  };

  return (
    <Component>
      <section>
        <label htmlFor="select.languages">
          {t('accessTokens.linkGenerator.languageLabel', { ns: 'components' })}
        </label>
        <select
          id="select.languages"
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
        >
          <option value="en">
            {t('accessTokens.linkGenerator.languages.en', { ns: 'components' })}
          </option>
          <option value="fi">
            {t('accessTokens.linkGenerator.languages.fi', { ns: 'components' })}
          </option>
          <option value="fr">
            {t('accessTokens.linkGenerator.languages.fr', { ns: 'components' })}
          </option>
          <option value="no">
            {t('accessTokens.linkGenerator.languages.no', { ns: 'components' })}
          </option>
          <option value="sv">
            {t('accessTokens.linkGenerator.languages.sv', { ns: 'components' })}
          </option>
          <option value="es">
            {t('accessTokens.linkGenerator.languages.es', { ns: 'components' })}
          </option>
          <option value="de">
            {t('accessTokens.linkGenerator.languages.de', { ns: 'components' })}
          </option>
        </select>
      </section>

      <section>
        <label htmlFor="select.services">
          {t('accessTokens.linkGenerator.serviceLabel', { ns: 'components' })}
        </label>
        <select
          id="select.services"
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value as Service)}
        >
          <option value={Service.Viewer}>
            {t('accessTokens.linkGenerator.services.viewer', { ns: 'components' })}
          </option>
          <option value={Service.GcpEditor}>
            {t('accessTokens.linkGenerator.services.gcpEditor', { ns: 'components' })}
          </option>
          <option value={Service.GeodataUpload}>
            {t('accessTokens.linkGenerator.services.geodataUpload', { ns: 'components' })}
          </option>
        </select>
      </section>

      <section>
        <label htmlFor="select.projects">
          {t('accessTokens.linkGenerator.projectLabel', { ns: 'components' })}
        </label>
        {renderProjects()}
      </section>

      {selectedService === Service.GcpEditor && (
        <section>
          <label htmlFor="select.geodata">
            {t('accessTokens.linkGenerator.geodataLabel', { ns: 'components' })}
          </label>
          {renderGeodata()}
        </section>
      )}

      {renderCreateButton()}
    </Component>
  );
};

const Component = styled.menu`
  width: 300px;
  display: flex;
  flex-direction: column;

  section {
    display: flex;
    flex-direction: column;
    margin: 0 0 15px 0;
  }

  label {
    display: block;
    font-weight: 500;
    font-size: 0.9em;
    margin-bottom: 5px;
  }

  select {
    padding: 3px;
  }

  .loading-error {
    color: #f00;
  }
`;

export { LinkGenerator };
